import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { motion } from "framer-motion";

export default function JarvisUI() {
  const [time, setTime] = useState(new Date());
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const hasPlayedAudio = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formattedTime = time.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const playVoiceResponse = (audioData) => {
    if (!audioData) return;
    const voice = new Audio(audioData.startsWith("data:") ? audioData : `data:audio/mpeg;base64,${audioData}`);
    voice.crossOrigin = "anonymous";
    voice.volume = 1;
    voice.play().catch((err) => console.error("❌ Voice playback error:", err));
    voice.onended = () => {
      if (audioRef.current) audioRef.current.volume = 0.15;
    };
  };

  const speakWithJarvis = (text) => {
    if (!text) return;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.pitch = 1.2;
    utterance.rate = 1;
    utterance.volume = 1;
    speechSynthesis.speak(utterance);
  };

  const sendToJarvis = async (inputText) => {
    try {
      const res = await fetch("https://merakistationeryco.app.n8n.cloud/webhook/n8n", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: inputText })
      });
      const data = await res.json();
      console.log("🤖 Jarvis replied:", data);

      if (data.audio) {
        playVoiceResponse(data.audio);
      } else if (data.output) {
        speakWithJarvis(data.output);
      } else {
        console.warn("⚠️ No output or audio returned from Jarvis");
      }
    } catch (err) {
      console.error("❌ Failed to contact Jarvis:", err);
    }
  };

  const handleVoiceCommand = () => {
    if (!hasPlayedAudio.current) {
      const bgAudio = new Audio("/jarvis-ambient.mp3");
      bgAudio.loop = true;
      bgAudio.volume = 1;
      bgAudio.play()
        .then(() => {
          console.log("🔊 Ambient music started");
          audioRef.current = bgAudio;
          setTimeout(() => sendToJarvis("Boot up"), 1000);
        })
        .catch(() => console.warn("⚠️ Autoplay blocked"));
      hasPlayedAudio.current = true;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setIsListening(true);
        console.log("🎙️ Listening...");
      };

      recognition.onresult = async (event) => {
        const transcript = event.results[0][0].transcript;
        console.log("🗣️ Heard:", transcript);
        if (audioRef.current) audioRef.current.volume = 0.05;
        await sendToJarvis(transcript);
        setIsListening(false);
      };

      recognition.onerror = (event) => {
        console.error("❌ Recognition error:", event);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log("🛑 Stopped listening");
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    if (!isListening) {
      try {
        recognitionRef.current.start();
      } catch {
        console.warn("⚠️ Already listening");
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center relative overflow-hidden px-4">

      {/* ✨ Classy glow background animation */}
      <div className="absolute w-[700px] h-[700px] bg-cyan-400 opacity-20 rounded-full blur-[120px] animate-ping z-0"></div>
      <div className="absolute w-[900px] h-[900px] bg-cyan-600 opacity-10 rounded-full blur-[180px] animate-pulse z-0"></div>

      {/* 🖼️ Fixed large center image */}
      <div className="z-10">
        <img
          src="/newone.png"
          alt="JARVIS"
          className="w-[450px] md:w-[500px] object-contain select-none pointer-events-none"
        />
      </div>

      {/* 🎙️ Mic Button */}
      <div className="absolute bottom-24 z-10">
        <button
          onClick={handleVoiceCommand}
          disabled={isListening}
          className="bg-cyan-600 hover:bg-cyan-400 text-black px-8 py-3 rounded-full shadow-xl flex items-center gap-2 font-semibold transition duration-300"
        >
          <Mic className="w-5 h-5" />
          {isListening ? "Listening..." : "Speak to Jarvis"}
        </button>
      </div>

      {/* 🕒 Clock */}
      <div className="absolute bottom-6 text-gray-500 text-sm tracking-widest z-10">
        {formattedTime}
      </div>
    </div>
  );
}
